/**
 * Session-scoped auth storage. Deliberately sessionStorage, not
 * localStorage or a cookie: closing the tab ends the owner's sign-in, and
 * nothing here survives a device restart.
 */

const KEY_TOKENS = "ledger.auth.tokens";
const KEY_FLASH = "ledger.auth.flash";

export interface StoredTokens {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiresAt: number;
  email: string | null;
}

export function isTokenValid(tokens: StoredTokens | null, now: number = Date.now()): tokens is StoredTokens {
  return tokens !== null && tokens.expiresAt > now;
}

export function readTokens(): StoredTokens | null {
  const raw = sessionStorage.getItem(KEY_TOKENS);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<StoredTokens>;
    if (
      typeof parsed.accessToken !== "string" ||
      typeof parsed.idToken !== "string" ||
      typeof parsed.refreshToken !== "string" ||
      typeof parsed.expiresAt !== "number" ||
      !Number.isFinite(parsed.expiresAt) ||
      (parsed.email !== null && typeof parsed.email !== "string")
    ) {
      throw new Error("Invalid stored session");
    }
    return parsed as StoredTokens;
  } catch {
    sessionStorage.removeItem(KEY_TOKENS);
    return null;
  }
}

export function writeTokens(tokens: StoredTokens): void {
  sessionStorage.setItem(KEY_TOKENS, JSON.stringify(tokens));
}

export function clearTokens(): void {
  sessionStorage.removeItem(KEY_TOKENS);
}

export function writeFlash(message: string): void {
  sessionStorage.setItem(KEY_FLASH, message);
}

export function readAndClearFlash(): string | null {
  const message = sessionStorage.getItem(KEY_FLASH);
  if (message) sessionStorage.removeItem(KEY_FLASH);
  return message;
}
